import { Alert, AlertDescription, Button } from "@zapier/design-system";
import { Panel } from "@xyflow/react";
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import {
  isAssetInProjectAsyncAtom,
  newAssetIdentifierAtom,
  projectAssetsAsyncAtom,
  ProjectAssetsError,
  addAssetToProjectSetterAtom,
  ProjectAssetType,
  projectOfAssetAsyncAtom,
  ProjectAssetsSuccess,
} from "@zapier/canvas-state";
import { Suspense, useCallback, useEffect, useState } from "react";

import { ErrorBoundary } from "../ErrorBoundary";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";

import styles from "./AddAssetToProjectAlert.module.css";

const PRIMARY_BUTTON_TEXT = "Yes, add to Project";
const SECONDARY_BUTTON_TEXT = "No thanks";

function SuspendedComponent() {
  const [newAssetIdentifier, setNewAssetIdentifier] = useAtom(
    newAssetIdentifierAtom,
  );

  const response = useAtomValue(projectAssetsAsyncAtom);
  const projectOfAsset = useAtomValue(projectOfAssetAsyncAtom);
  const isAssetInAnotherProject = Boolean(projectOfAsset);
  const { emitEventOnAddingAssetToProject } = useEmitUserInteractionEvents();
  const addAssetToProject = useSetAtom(addAssetToProjectSetterAtom);
  const isAssetInProjectAlready = useAtomValue(isAssetInProjectAsyncAtom);
  const [hasError, setHasError] = useState<boolean>(false);

  const dismiss = useCallback(
    (rawText: string, assetType: ProjectAssetType) => {
      emitEventOnAddingAssetToProject(assetType, rawText);
      setNewAssetIdentifier(null);
    },
    [setNewAssetIdentifier, emitEventOnAddingAssetToProject],
  );

  const handleAddAssetToProject = useCallback(async () => {
    try {
      const { error } = await addAssetToProject();

      if (error) {
        setHasError(true);
      } else {
        setHasError(false);
      }
    } catch (error) {
      setHasError(true);
    }
  }, [addAssetToProject, setHasError]);

  useEffect(() => {
    if (
      (response as ProjectAssetsError).error ||
      !("project" in response) ||
      isAssetInAnotherProject ||
      isAssetInProjectAlready
    ) {
      setNewAssetIdentifier(null);
    }
  }, [
    response,
    isAssetInAnotherProject,
    isAssetInProjectAlready,
    setNewAssetIdentifier,
  ]);

  // This happens when newAssetIdentifier is truthy after selecting a Zap and then is set to null by the useEffect above
  if (!newAssetIdentifier) {
    return null;
  }

  const [assetType] = newAssetIdentifier.split(":") as [ProjectAssetType];

  if ((response as ProjectAssetsSuccess).project) {
    const capitalizedAssetType = capitalizeFirstLetter(assetType);

    if (hasError) {
      return (
        <Alert
          status="error"
          onDismiss={() => dismiss("x", assetType)}
          title={`We couldn't add this ${capitalizedAssetType} to the “${
            (response as ProjectAssetsSuccess).project.title
          }” Project`}
        >
          <AlertDescription>Please try again later.</AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert
        onDismiss={() => dismiss("x", assetType)}
        title={`Add this ${capitalizedAssetType} to the “${
          (response as ProjectAssetsSuccess).project.title
        }” Project?`}
      >
        <AlertDescription>
          Doing so will inherit the project&apos;s user permissions.
        </AlertDescription>
        <div className={styles.buttonWrapper}>
          <Button
            color="secondary"
            size="small"
            onClick={() => dismiss(SECONDARY_BUTTON_TEXT, assetType)}
          >
            {SECONDARY_BUTTON_TEXT}
          </Button>
          <Button
            color="primary"
            size="small"
            onClick={() => handleAddAssetToProject()}
          >
            {PRIMARY_BUTTON_TEXT}
          </Button>
        </div>
      </Alert>
    );
  }
}

export default function AddAssetToProjectAlert() {
  return (
    <Panel position="top-center">
      <ErrorBoundary>
        <Suspense fallback={null}>
          <SuspendedComponent />
        </Suspense>
      </ErrorBoundary>
    </Panel>
  );
}
